import { postJSON } from "../api.js";
import { renderTab } from "../app/shell.js";
import { esc, opt } from "../ui/format.js";
import { uiCard, uiEmpty, uiFinding, uiForecastReadout, uiGet, uiHeader, uiKpis, uiMoney, uiPct, uiPill, uiPlanDrawer, uiRing, uiSpark, uiTabs, uiTone, uiTrendChart, uiWireTabs } from "../ui/kit.js";
import { toast } from "../ui/modals.js";
import { ccTabCustomers, ccTabFinancials, ccTabMarket, ccTabRoadmap, wireProductRoadmap } from "./core/products.js";

/**
 * Products — redesigned portfolio and Command Center (replaces app.js's
 * renderProducts / renderProductCommandCenter).
 *
 * Portfolio: compact product cards (health, trend, the one issue that matters,
 * the recommended move) instead of eleven buttons repeated on every product.
 * Command Center: Overview (diagnosis + recommended moves), Playbook (every
 * move grouped by objective, each opening a Plan drawer with a real 12-month
 * forecast before committing), plus the existing deep tabs.
 * Data: /api/products, /api/products/{id}/playbook|history|archetype|market-intelligence.
 */

let pxOpenId = null;
let pxTab = "overview";

const PX_STAGE_TONE = { Development: "info", Launch: "info", Growth: "good", Maturity: "", Decline: "bad",
                        Sunsetting: "warn", Retired: "" };

function pxOpenProduct(id, tab = "overview") {
  pxOpenId = id;
  pxTab = tab;
  const m = document.getElementById("main-content");
  if (m) m.scrollTop = 0;
  renderTab();
}

// ---------------------------------------------------------------- Plan drawer for one play

function pxCostFor(play, value) {
  if (play.cost !== null && play.cost !== undefined) return play.cost;
  if (!play.input) return 0;
  if (play.id === "factory") return value * (play.capex_per_unit || 0);
  if (play.input.kind === "amount") return value;
  if (play.input.kind === "choice") {
    const opt = (play.input.options || []).find((o) => o.value === value);
    return opt ? opt.cost : null;
  }
  return 0;
}

function pxCommitBody(play, value) {
  const body = {};
  for (const [k, v] of Object.entries(play.commit.body)) {
    if (v === "{value}") body[k] = value;
    else if (v === "{value_fraction}") body[k] = value / 100;
    else if (v === "{value_int}") body[k] = Math.round(value);
    else if (v === "{factory_capex}") body[k] = Math.round(value) * (play.capex_per_unit || 0);
    else body[k] = v;
  }
  return body;
}

function pxOpenPlay(product, play) {
  if (play.disabled) { toast(play.disabled, true); return; }
  uiPlanDrawer({
    eyebrow: `${product.name} · ${play.group === "lifecycle" ? "Lifecycle move" : "Plan a move"}`,
    title: play.label,
    description: play.description,
    effect: play.effect,
    risk: play.risk,
    input: play.input,
    commitLabel: play.group === "lifecycle" && play.id !== "relaunch" ? "Confirm — this can't be undone" : "Commit",
    costFor: (v) => pxCostFor(play, v),
    forecast: play.scenario ? (v, { signal } = {}) => postJSON(`/products/${product.id}/playbook/preview`, { play: play.id, value: v }, { signal }) : null,
    renderForecast: uiForecastReadout,
    // a price change also gets the company-wide Ghost Future (Phase 7)
    ghost: play.id === "price" ? (v) => (v ? { type: "adjust_pricing", params: { product_name: product.name, price_change_pct: v / 100 } } : null) : null,
    commit: async (v) => (await postJSON(play.commit.endpoint, pxCommitBody(play, v))).message,
  });
}

function pxPlayTile(play) {
  const cost = play.cost !== null && play.cost !== undefined
    ? (play.cost > 0 ? uiMoney(play.cost, { compact: true }) : "Free")
    : (play.cost_note || "You choose");
  return `
    <button type="button" class="sx-play${play.recommended ? " recommended" : ""}" data-play="${esc(play.id)}" ${play.disabled ? "disabled" : ""}>
      ${play.recommended ? `<span class="sx-badge">Recommended</span>` : ""}
      <span class="sx-play-label">${esc(play.label)}</span>
      <span class="sx-play-desc">${esc(play.description)}</span>
      ${play.recommended && play.reason ? `<span class="sx-play-why">Why: ${esc(play.reason)}</span>` : ""}
      ${play.disabled ? `<span class="sx-play-disabled">${esc(play.disabled)}</span>` : ""}
      <span class="sx-play-foot"><span>${esc(play.effect.split(/[—.;]/)[0])}</span><strong>${esc(cost)}</strong></span>
    </button>`;
}

function pxWirePlays(root, product, book) {
  const plays = Object.fromEntries(book.groups.flatMap((g) => g.plays).map((p) => [p.id, p]));
  root.querySelectorAll("[data-play]").forEach((btn) => {
    btn.addEventListener("click", () => pxOpenPlay(product, plays[btn.dataset.play]));
  });
}

// ---------------------------------------------------------------- Portfolio

/** ⭐ 4.3 · 1,240 reviews (backend/product_reviews.py); tap for what customers say. */
function pxStars(r) {
  if (!r) return "";
  const full = Math.round(r.rating);
  return `<button type="button" class="px-stars" data-px-reviews="${r.product_id}" title="${esc(r.word)}: see what customers say">
    <span class="px-star-row">${"★".repeat(full)}${"☆".repeat(5 - full)}</span><b>${r.rating.toFixed(1)}</b><em>${r.count.toLocaleString("en-US")} reviews · ${esc(r.word)}</em></button>`;
}

function pxReviewsHtml(r) {
  return `<div class="px-revs">${r.reviews.map((x) => `<blockquote class="px-rev"><span class="px-star-row">${"★".repeat(x.stars)}${"☆".repeat(5 - x.stars)}</span>
    “${esc(x.text)}”<cite>${esc(x.name)} · month ${x.month}</cite></blockquote>`).join("")}</div><p class="sx-card-sub">${esc(r.why)}</p>`;
}

function pxOpenReviews(r) {
  const back = document.createElement("div");
  back.className = "modal-backdrop";
  back.innerHTML = `<div class="modal px-rev-modal" role="dialog" aria-label="Reviews">
    <button type="button" class="modal-x" data-x aria-label="Close">✕</button>
    <h3>${esc(r.name)}: ${r.rating.toFixed(1)} ★ <span class="sx-card-sub">${r.count.toLocaleString("en-US")} reviews · ${esc(r.word)}</span></h3>
    ${pxReviewsHtml(r)}</div>`;
  document.body.appendChild(back);
  back.addEventListener("click", (e) => { if (e.target === back || e.target.closest("[data-x]")) back.remove(); });
}

function pxCard(p, book, history, rv) {
  const top = (book && book.diagnosis && book.diagnosis[0]) || null;
  const rec = book ? book.groups.flatMap((g) => g.plays).find((pl) => pl.recommended) : null;
  const revs = (history || []).map((h) => h.revenue);
  return `
    <article class="sx-card px-card">
      <div class="px-card-top">
        ${uiRing(p.health_score, "Health", 58)}
        <div style="min-width:0; flex:1;">
          <h3 class="px-card-name">${esc(p.name)}</h3>
          <div class="px-card-meta">v${p.version} · ${esc(p.tier_label || p.tier)} · ${esc(p.category)}</div>
          ${pxIdentity(p)}
        </div>
        ${uiPill(p.status, PX_STAGE_TONE[p.status] || "")}
      </div>
      <div class="px-card-stats">
        <div class="px-stat"><span>Revenue/mo</span><strong>${uiMoney(p.monthly_revenue, { compact: true })}</strong></div>
        <div class="px-stat"><span>Growth</span><strong class="${uiTone(p.growth_pct_monthly)}">${uiPct(p.growth_pct_monthly, 1, true)}</strong></div>
        <div class="px-stat"><span>${esc(p.customers_label || "Customers")}</span><strong>${(p.estimated_customers || 0).toLocaleString("en-US")}</strong></div>
      </div>
      ${pxStars(rv)}
      ${pxLife(p.status)}
      ${uiSpark(revs, { width: 280, height: 36 })}
      ${top && top.severity >= 1 ? uiFinding(top) : top ? uiFinding(top) : ""}
      <div class="px-card-actions">
        <button type="button" data-open="${p.id}">Open Command Center</button>
        ${rec ? `<button type="button" class="primary" data-quick="${p.id}" data-quick-play="${esc(rec.id)}">▶ ${esc(rec.label.length > 30 ? rec.label.slice(0, 28) + "…" : rec.label)}</button>` : ""}
      </div>
    </article>`;
}

window.pxOpenProduct = pxOpenProduct; // the Products 3D home opens a product with it

// ---------------------------------------------------------------- portfolio pieces (2026-10-15 redesign)

const PX_STAGES = ["Launch", "Growth", "Maturity", "Decline"];
const PX_COLORS = ["#7ee8fa", "#a78bfa", "#34d399", "#fbbf24", "#fb7185", "#5eead4", "#f472b6", "#93c5fd"];
let pxSort = "revenue";

/** Where a product is in its life: four steps, the current one lit. */
function pxLife(status) {
  const i = PX_STAGES.findIndex((x) => String(status || "").startsWith(x.slice(0, 4)));
  return `<div class="px-life" title="Lifecycle: ${esc(status)}">${PX_STAGES.map((x, k) => `<span class="${k === i ? "on" : k < i ? "past" : ""}">${x}</span>`).join("")}</div>`;
}

/** One bar: each product's share of this month's product revenue. */
function pxMix(live, revenue) {
  if (live.length < 2 || revenue <= 0) return "";
  const rows = live.slice().sort((a, b) => (b.monthly_revenue || 0) - (a.monthly_revenue || 0));
  return `<section class="px-mix" aria-label="Revenue mix">
    <div class="px-mix-bar">${rows.map((p, i) => `<i style="width:${(100 * (p.monthly_revenue || 0)) / revenue}%; background:${PX_COLORS[i % PX_COLORS.length]}" title="${esc(p.name)}: ${uiMoney(p.monthly_revenue, { compact: true })}/mo"></i>`).join("")}</div>
    <div class="px-mix-legend">${rows.map((p, i) => `<span><i style="background:${PX_COLORS[i % PX_COLORS.length]}"></i>${esc(p.name)} <b>${Math.round((100 * (p.monthly_revenue || 0)) / revenue)}%</b></span>`).join("")}</div>
  </section>`;
}

/** Finished R&D projects waiting to become products (rnd_lab.innovations). */
function pxFromLab(list) {
  const ready = (list || []).filter((v) => v.status !== "launched");
  if (!ready.length) return "";
  return `<section class="px-lab">
    <div class="px-lab-head"><span>💡</span><div><b>From your R&amp;D Lab: ready to launch</b><em>Already working inside the company. Launch one and customers get it too.</em></div></div>
    <div class="px-lab-grid">${ready.map((v) => `<div class="px-lab-card"><span class="px-lab-kind">${esc(v.kind)} · tier ${v.tier}</span><b>${esc(v.title)}</b>
      <span>For customers: ${esc(v.launch_bonus)}</span><span class="px-lab-in">In the company: ${esc(v.company_effect)}</span>
      <button type="button" class="primary" data-px-rnd="${v.id}">🚀 Launch it for customers</button></div>`).join("")}</div>
  </section>`;
}

/**
 * Products' profit is before company-wide costs; Financials' is after. Show the bridge so a big product profit
 * next to a company loss doesn't look like a bug ($24.9B product profit vs a $226M loss in Financials).
 */
function pxBridge(profit, snap) {
  const is = snap && snap.income_statement;
  if (!is || !profit) return "";
  const net = is.net_income || 0, gap = profit - net;
  if (Math.abs(gap) < Math.max(1000, Math.abs(profit) * 0.02)) return "";
  const parts = [["R&D", is.rnd], ["running the company (G&A)", is.gna], ["interest", is.interest], ["tax", is.tax]]
    .filter(([, v]) => (v || 0) > 0).map(([l, v]) => `${l} ${uiMoney(v, { compact: true })}`).join(", ");
  return `<p class="px-bridge sx-card-sub">Product profit <b>${uiMoney(profit, { compact: true })}</b> is before company-wide costs.
    ${gap > 0 ? `Minus <b>${uiMoney(gap, { compact: true })}</b> of costs outside the products${parts ? ` (the company's books show ${parts})` : ""}` : `Plus <b>${uiMoney(-gap, { compact: true })}</b> of other income`}
    = <b class="${net >= 0 ? "pos" : "neg"}">${uiMoney(net, { compact: true, signed: true })}</b> net for the company, as in Financials (month ${snap.month}).</p>`;
}

/** The product's codename and colourway (2.7, Product Forge): three swatches and the names. */
function pxIdentity(p) {
  if (!p.codename && !p.colourway) return "";
  const cw = p.colourway || { name: "", colours: [] };
  return `<div class="px-ident" title="Codename from the Forge workshop · colourway ${esc(cw.name)}">
    <span class="px-swatches" aria-hidden="true">${cw.colours.map((c) => `<i style="background:${esc(c)}"></i>`).join("")}</span>
    <span>${esc(p.codename || "")}${cw.name ? ` · ${esc(cw.name)} colourway` : ""}</span></div>`;
}

window.renderProducts = async function (main, snap) {
  if (pxOpenId !== null) return pxRenderCommandCenter(main, snap);
  const [products, factories, lab, revs] = await Promise.all([uiGet("/products"), uiGet("/factories").catch(() => []), uiGet("/rnd").catch(() => null),
    uiGet("/products/reviews").catch(() => null)]);
  const reviews = Object.fromEntries(((revs && revs.products) || []).map((r) => [r.product_id, r]));
  const innovations = (lab && lab.innovations) || [];
  // a signed customer contract earns through a revenue line of its own, but it isn't a product (2.7): it lives in Deals
  const isContract = (p) => p.category === "Enterprise Contract";
  const contracts = products.filter((p) => p.active && isContract(p));
  const live = products.filter((p) => p.active && !isContract(p));
  const retired = products.filter((p) => !p.active && !isContract(p));
  const cRevenue = contracts.reduce((s, p) => s + (p.monthly_revenue || 0), 0);
  const cProfit = contracts.reduce((s, p) => s + (p.monthly_profit || 0), 0);
  // one request for every product's playbook and history (it used to be two per product)
  const port = (await uiGet("/products/portfolio?months=12").catch(() => null)) || { products: {} };
  const books = live.map((p) => (port.products[p.id] || {}).playbook || null);
  const histories = live.map((p) => (port.products[p.id] || {}).history || []);
  const revenue = live.reduce((s, p) => s + (p.monthly_revenue || 0), 0);
  const profit = live.reduce((s, p) => s + (p.monthly_profit || 0), 0);
  const avgHealth = live.length ? Math.round(live.reduce((s, p) => s + p.health_score, 0) / live.length) : 0;
  // only real trouble counts (losing money, shrinking, customers leaving fast): when 30 of 31 products were
  // flagged, players learned to ignore the flag. Severity 2 is "worth a look", shown on the card only.
  const attention = books.filter((b) => b && b.diagnosis.some((f) => f.severity >= 3)).length;

  const order = live.map((p, i) => i);
  const key = { revenue: (i) => -(live[i].monthly_revenue || 0), growth: (i) => -(live[i].growth_pct_monthly || 0),
    health: (i) => live[i].health_score, attention: (i) => -((books[i] && books[i].diagnosis.reduce((m, f) => Math.max(m, f.severity), 0)) || 0) };
  order.sort((a, b) => key[pxSort](a) - key[pxSort](b));
  main.innerHTML = `
    <div class="sx">
      ${uiHeader(`Products — Month ${snap.month}`, "Every product is its own business. Launch new ones here; open one to see what's holding it back and the moves that would fix it.")}
      <section class="px-hero">
        <div><h3>${live.length ? "Grow the line-up" : "Launch your first product"}</h3>
          <p>${live.length ? `${live.length} product${live.length === 1 ? "" : "s"} on sale${innovations.some((v) => v.status !== "launched") ? " · your R&D Lab has something ready to launch" : ""}.` : "Nothing earns until you sell something. Pick what it is, its specs and its budget."}</p></div>
        <div class="px-hero-go"><button type="button" class="primary" id="px-launch">🚀 Launch a new product</button><button type="button" id="px-quick">⚡ Quick Build</button></div>
      </section>
      ${uiKpis([
        { label: "Portfolio revenue", value: `${uiMoney(revenue, { compact: true })}/mo` },
        { label: "Product profit", value: `${uiMoney(profit, { compact: true })}/mo`, tone: uiTone(profit), hint: "Contribution basis, before company overhead" },
        { label: "Live products", value: String(live.length) },
        { label: "Average health", value: `${avgHealth}/100`, tone: avgHealth >= 60 ? "pos" : avgHealth < 40 ? "neg" : "" },
        { label: "Need attention", value: String(attention), tone: attention ? "neg" : "pos", hint: "Losing money, shrinking, or customers leaving fast" },
      ])}
      ${contracts.length ? `<p class="px-contracts">Plus <b>${contracts.length} customer contract${contracts.length === 1 ? "" : "s"}</b> earning <b>${uiMoney(cRevenue, { compact: true })}</b> a month: they live in <button type="button" class="linklike" data-px-deals>Deals &amp; Partners → Contracts</button>.</p>` : ""}
      ${pxBridge(profit + cProfit, snap)}
      ${pxFromLab(innovations)}
      ${pxMix(live, revenue)}
      ${live.length > 1 ? `<div class="px-sort" role="group" aria-label="Sort products">${[["revenue", "Biggest"], ["growth", "Fastest growing"], ["attention", "Needs attention"], ["health", "Weakest health"]]
        .map(([k, l]) => `<button type="button" class="${pxSort === k ? "on" : ""}" data-px-sort="${k}">${l}</button>`).join("")}</div>` : ""}
      ${live.length ? `<div class="px-grid">${order.map((i) => pxCard(live[i], books[i], histories[i], reviews[live[i].id])).join("")}</div>`
        : uiEmpty("No products yet", "Customers pay for products. Launch your first one — the preview shows exactly what it will cost and earn.",
                  `<button type="button" class="primary" id="px-launch-empty">Launch a Product</button>`)}
      ${live.length ? `<div id="px-forge"></div>` : ""}
      ${factories.length ? uiCard("Production capacity", `
        <table class="sx-table">
          <tr><th>Facility</th><th>Status</th><th class="num">Capacity</th><th class="num">Utilization</th><th class="num">Downtime risk</th><th class="num">Defects</th></tr>
          ${factories.map((f) => `<tr><td>${esc(f.name)}</td><td>${uiPill(f.status, f.status === "Operational" ? "good" : "warn")}</td>
            <td class="num">${(f.capacity_units || 0).toLocaleString("en-US")}/mo</td>
            <td class="num ${f.utilization_pct >= 90 ? "neg" : ""}">${uiPct(f.utilization_pct, 0)}</td>
            <td class="num">${uiPct(f.downtime_risk_pct, 1)}</td><td class="num">${uiPct(f.defect_rate_pct, 1)}</td></tr>`).join("")}
        </table>`, { sub: "Factories cap how much you can sell; maintenance and automation live in each product's playbook." }) : ""}
      ${retired.length ? `<details class="sx-card"><summary>Retired products (${retired.length})</summary>
        <table class="sx-table" style="margin-top:10px;">${retired.map((p) => `<tr><td>${esc(p.name)}</td><td>${esc(p.category)}</td><td class="num">launched M${p.launch_month}</td></tr>`).join("")}</table></details>` : ""}
    </div>`;

  const launch = () => window.openLaunchProductModal();
  main.querySelector("#px-launch")?.addEventListener("click", launch);
  main.querySelector("#px-launch-empty")?.addEventListener("click", launch);
  main.querySelector("[data-px-deals]")?.addEventListener("click", () => {
    try { localStorage.setItem("ges.deals.tab", "contracts"); } catch (e) { /* not remembered */ }
    if (window.ges && window.ges.showTab) window.ges.showTab("deals");
  });
  main.querySelector("#px-quick")?.addEventListener("click", () => window.fgQuickBuild());
  main.querySelectorAll("[data-px-reviews]").forEach((b) => b.addEventListener("click", () => pxOpenReviews(reviews[+b.dataset.pxReviews])));
  main.querySelectorAll("[data-px-sort]").forEach((b) => b.addEventListener("click", () => { pxSort = b.dataset.pxSort; renderTab(); }));
  main.querySelectorAll("[data-px-rnd]").forEach((b) => b.addEventListener("click", () => {
    window.openLaunchProductModal({ innovation: innovations.find((v) => v.id === +b.dataset.pxRnd) });
  }));
  if (typeof window.fgRenderPortfolio === "function") window.fgRenderPortfolio(main.querySelector("#px-forge")).catch(() => {});
  main.querySelectorAll("[data-open]").forEach((b) => b.addEventListener("click", () => pxOpenProduct(parseInt(b.dataset.open, 10))));
  main.querySelectorAll("[data-quick]").forEach((b) => b.addEventListener("click", () => {
    const idx = live.findIndex((p) => p.id === parseInt(b.dataset.quick, 10));
    const play = books[idx].groups.flatMap((g) => g.plays).find((pl) => pl.id === b.dataset.quickPlay);
    pxOpenPlay(live[idx], play);
  }));
};

// ---------------------------------------------------------------- Command Center

const PX_TABS = [["overview", "Overview"], ["playbook", "Playbook"], ["market", "Market & Customers"],
                 ["roadmap", "Roadmap"], ["financials", "Unit economics"]];

async function pxRenderCommandCenter(main, snap) {
  const products = await uiGet("/products");
  const p = products.find((x) => x.id === pxOpenId);
  if (!p) { pxOpenId = null; return window.renderProducts(main, snap); }
  const [book, hist, archetype, market, revData] = await Promise.all([
    uiGet(`/products/${p.id}/playbook`),
    uiGet(`/products/${p.id}/history?months=24`).then((h) => h.history).catch(() => []),
    uiGet(`/products/${p.id}/archetype`).catch(() => ({})),
    uiGet(`/products/${p.id}/market-intelligence`).catch(() => ({})),
    uiGet("/products/reviews").catch(() => null),
  ]);
  const rv = ((revData && revData.products) || []).find((r) => r.product_id === p.id);
  const revs = hist.map((h) => h.revenue);

  main.innerHTML = `
    <div class="sx">
      <button type="button" id="px-back" style="align-self:flex-start; font-size:12px; padding:6px 12px;">← All products</button>
      <div class="px-cc-head">
        <div class="px-cc-name">
          ${uiRing(p.health_score, "Health", 72)}
          <div>
            <h2 class="sx-title">${esc(p.name)} <span style="font-size:14px; color:var(--text-low); font-family:var(--font-mono);">v${p.version}</span></h2>
            <div class="px-card-meta">${esc(archetype.archetype_label || p.archetype_label || "")} · ${esc(p.tier_label || p.tier)} · ${esc(p.category)} · launched M${p.launch_month}</div>
            ${pxIdentity(p)}
          </div>
        </div>
        ${uiPill(p.status, PX_STAGE_TONE[p.status] || "")}
      </div>
      ${uiKpis([
        { label: "Revenue/mo", value: uiMoney(p.monthly_revenue, { compact: true }) },
        { label: "Growth", value: uiPct(p.growth_pct_monthly, 1, true), tone: uiTone(p.growth_pct_monthly) },
        { label: "Profit/mo", value: uiMoney(p.monthly_profit, { compact: true }), tone: uiTone(p.monthly_profit) },
        { label: p.customers_label || "Customers", value: (p.estimated_customers || 0).toLocaleString("en-US") },
        { label: p.satisfaction_label || "Satisfaction", value: `${Math.round(p.satisfaction)}/100`, tone: p.satisfaction >= 75 ? "pos" : p.satisfaction < 60 ? "neg" : "" },
        { label: p.churn_label || "Churn", value: uiPct(p.churn_rate_pct, 1) + "/mo", tone: p.churn_rate_pct >= 2 ? "neg" : "" },
        { label: "Market captured", value: uiPct(p.market_penetration_pct, 0) },
      ])}
      ${uiTabs(PX_TABS, pxTab, "px")}
      <div id="px-tab-body"></div>
    </div>`;
  main.querySelector("#px-back").addEventListener("click", () => { pxOpenId = null; renderTab(); });
  uiWireTabs(main, "px", (key) => { pxTab = key; renderTab(); });

  const body = main.querySelector("#px-tab-body");
  if (pxTab === "overview") {
    const recommended = book.groups.flatMap((g) => g.plays).filter((pl) => pl.recommended);
    body.innerHTML = `
      <div class="sx-grid-2">
        <div style="display:flex; flex-direction:column; gap:18px;">
          ${uiCard("Revenue trend", revs.length >= 2 ? uiTrendChart(revs, { labels: hist.map((h) => `M${h.month}`) }) : uiEmpty("Not enough history yet", "The trend appears after a couple of months live."),
                   { sub: `Last ${revs.length} months, as actually booked` })}
          ${uiCard("What's holding it back", `<div class="sx-findings">${book.diagnosis.map(uiFinding).join("")}</div>`)}
          ${rv ? uiCard(`What customers say · ${rv.rating.toFixed(1)} ★`, pxReviewsHtml(rv), { sub: `${rv.count.toLocaleString("en-US")} reviews · ${rv.word}` }) : ""}
        </div>
        <div style="display:flex; flex-direction:column; gap:18px;">
          ${uiCard("Best moves by forecast", `<div id="px-ranking" class="sx-forecast-loading">Running every available move through the next 12 months on a copy of your company…</div>`,
                   { sub: "Each move at its suggested size, ranked by what it adds to company profit over 12 months" })}
          ${uiCard("Recommended by the diagnosis", recommended.length
            ? `<div class="sx-plays" style="grid-template-columns:1fr;">${recommended.map(pxPlayTile).join("")}</div>`
            : uiEmpty("Nothing urgent", "This product is in good shape. The full playbook has every available move."),
            { actions: `<button type="button" id="px-all-moves" style="font-size:12px; padding:6px 12px;">All moves →</button>` })}
        </div>
      </div>`;
    body.querySelector("#px-all-moves")?.addEventListener("click", () => { pxTab = "playbook"; renderTab(); });
    pxWirePlays(body, p, book);
    pxLoadRanking(body.querySelector("#px-ranking"), p, book);
  } else if (pxTab === "playbook") {
    body.innerHTML = book.groups.map((g) => `
      <section class="sx-playgroup">
        <div class="sx-playgroup-head"><h3 class="sx-playgroup-title">${esc(g.title)}</h3><span class="sx-playgroup-sub">${esc(g.subtitle)}</span></div>
        <div class="sx-plays">${g.plays.map(pxPlayTile).join("")}</div>
      </section>`).join("");
    body.style.display = "flex"; body.style.flexDirection = "column"; body.style.gap = "22px";
    pxWirePlays(body, p, book);
  } else if (pxTab === "market") {
    body.innerHTML = `<div class="sx-grid-2"><div>${ccTabMarket(p, market, archetype)}</div><div>${ccTabCustomers(p)}</div></div>`;
  } else if (pxTab === "roadmap") {
    body.innerHTML = ccTabRoadmap(p);
    wireProductRoadmap(p);
  } else if (pxTab === "financials") {
    body.innerHTML = ccTabFinancials(p);
  }
}

async function pxLoadRanking(host, product, book) {
  if (!host) return;
  let r;
  try { r = await uiGet(`/products/${product.id}/playbook/ranking`); } catch (e) {
    host.textContent = e.message || "No forecast available.";
    return;
  }
  if (!host.isConnected) return;
  if (!r.moves.length) { host.innerHTML = uiEmpty("No forecastable moves", "Nothing available can be forecast right now."); return; }
  const plays = Object.fromEntries(book.groups.flatMap((g) => g.plays).map((pl) => [pl.id, pl]));
  host.className = "";
  host.innerHTML = `
    <table class="sx-table">
      <tr><th>Move</th><th class="num">Profit (12 mo)</th><th class="num">Cash</th><th></th></tr>
      ${r.moves.map((m, i) => `
        <tr>
          <td>${i === 0 && m.profit_delta > 0 ? "🏆 " : ""}${esc(m.label)}</td>
          <td class="num ${uiTone(m.profit_delta)}">${uiMoney(m.profit_delta, { signed: true, compact: true })}</td>
          <td class="num ${uiTone(m.cash_delta)}">${uiMoney(m.cash_delta, { signed: true, compact: true })}</td>
          <td style="text-align:right;"><button type="button" data-rank-play="${esc(m.play)}" style="font-size:12px; padding:5px 12px;">Plan</button></td>
        </tr>`).join("")}
    </table>`;
  host.querySelectorAll("[data-rank-play]").forEach((b) => b.addEventListener("click", () => pxOpenPlay(product, plays[b.dataset.rankPlay])));
}

window.renderProductCommandCenter = pxRenderCommandCenter;
